import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { appendKeys, getCredentials, getInstance, markRecovered, resendNotification } from "../../api/fleet";
import { createKiroApiKey, markFleetConsumed, recommendUsers } from "../../api/users";
import type { CopyableFieldDto, FleetApiKeyEntry, FleetInstanceViewDto, RecommendUserCandidateDto } from "../../api/types";
import { useAsync } from "../../hooks/useAsync";
import { useAuth } from "../../auth/AuthContext";
import { formatBeijingDate, formatBeijingTime } from "../../utils/datetime";

const MAX_APPEND_KEYS = 100;
const REVEAL_TIMEOUT_MS = 15000;
const TIERS = ["pro", "proplus", "promax", "power"];

interface KeyEntryDraft {
  key: string;
  email: string;
}

function emptyKeyEntry(): KeyEntryDraft {
  return { key: "", email: "" };
}

/** 简单邮箱格式校验,与 backend validators.validate_assigned_user_email 的规则对齐。 */
function isValidEmail(email: string): boolean {
  if (!email || email.length > 254) return false;
  const at = email.indexOf("@");
  if (at <= 0 || at !== email.lastIndexOf("@")) return false;
  return at < email.length - 1;
}

function CopyButton({ text, disabled }: { text: string; disabled?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (disabled) {
    return (
      <button disabled style={{ fontSize: 11, padding: "2px 6px" }}>
        复制
      </button>
    );
  }
  return (
    <button
      style={{ fontSize: 11, padding: "2px 6px" }}
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
    >
      {copied ? "已复制" : "复制"}
    </button>
  );
}

function CopyableRow({ label, field }: { label: string; field: CopyableFieldDto }) {
  return (
    <div className="form-row" style={{ alignItems: "center" }}>
      <label>{label}</label>
      <span style={{ fontFamily: "monospace", flex: "1 1 auto", wordBreak: "break-all" }}>{field.value}</span>
      <CopyButton text={field.value} disabled={!field.copyable} />
      {field.caption && <span style={{ color: "#dc2626", fontSize: 12 }}>{field.caption}</span>}
    </div>
  );
}

/** 一个 API 端点子行(基础地址 + 后缀路径),复制整条完整地址。 */
function EndpointRow({ label, base, suffix, hint }: { label: string; base: string; suffix: string; hint: string }) {
  const available = base && base !== "-";
  const full = available ? `${base}${suffix}` : "-";
  return (
    <div className="form-row" style={{ alignItems: "center" }}>
      <label>{label}</label>
      <span style={{ fontFamily: "monospace", flex: "1 1 auto", wordBreak: "break-all" }}>{full}</span>
      <CopyButton text={full} disabled={!available} />
      <span style={{ color: "#64748b", fontSize: 11 }}>{hint}</span>
    </div>
  );
}

/**
 * 管理密码字段:默认掩码,点击"显示"才调用 getCredentials 拉明文,15 秒后自动
 * 恢复掩码。布局用 inline-flex + gap,避免掩码点与明文/按钮重叠。
 */
function AdminPasswordCell({ instanceId, field }: { instanceId: string; field: CopyableFieldDto }) {
  const { isAdmin } = useAuth();
  const [revealed, setRevealed] = useState<CopyableFieldDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReveal = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getCredentials(instanceId);
      setRevealed(resp.kiro_go_admin_password);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setRevealed(null), REVEAL_TIMEOUT_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取密码失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-row" style={{ alignItems: "center" }}>
      <label>Kiro_Go_Admin_Password</label>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "1 1 auto", flexWrap: "wrap" }}>
        <span style={{ fontFamily: "monospace", minWidth: 90 }}>{revealed ? revealed.value : "••••••••"}</span>
        {isAdmin && !revealed && (
          <button onClick={handleReveal} disabled={loading} style={{ fontSize: 11, padding: "2px 6px" }}>
            {loading ? "加载中..." : "显示"}
          </button>
        )}
        {revealed && <CopyButton text={revealed.value} disabled={!revealed.copyable} />}
        {error && <span style={{ color: "#dc2626", fontSize: 11 }}>{error}</span>}
        <span style={{ color: "#94a3b8", fontSize: 11 }}>(该管理面板登录仅需密码,无独立用户名)</span>
      </span>
      {field.caption && <span style={{ color: "#dc2626", fontSize: 12 }}>{field.caption}</span>}
    </div>
  );
}

/** Kiro_API_Key 明细表:每个 Key 的关联邮箱、Key Name、到期日、数值、来源;创建绑定的排在最前(后端已排序)。 */
function KiroApiKeySection({ entries }: { entries: FleetApiKeyEntry[] }) {
  if (!entries || entries.length === 0) {
    return <p style={{ color: "#64748b", fontSize: 13 }}>暂无 Kiro_API_Key 明细(仅 Admin + 实例 READY 时可见)。</p>;
  }
  return (
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>来源</th>
          <th>关联 Kiro 账号邮箱</th>
          <th>Key Name</th>
          <th>到期日</th>
          <th>Kiro_API_Key 数值</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e, idx) => (
          <tr key={`${e.key}-${idx}`}>
            <td>{idx + 1}</td>
            <td>
              {e.source === "topup" ? (
                <span className="badge badge-pending">充值</span>
              ) : (
                <span className="badge badge-active">创建绑定</span>
              )}
            </td>
            <td style={{ fontFamily: "monospace", fontSize: 12 }}>{e.kiro_user_email ?? "-"}</td>
            <td style={{ fontSize: 12 }}>{e.key_name ?? "-"}</td>
            <td style={{ fontSize: 12 }}>{e.expires_at ? formatBeijingDate(e.expires_at) : "-"}</td>
            <td style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>{e.key}</span>
              <CopyButton text={e.key} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 场景1：推荐用户条目 */
interface AutoCreateEntry {
  account_id: string;
  email: string;
  username: string;
}

type AppendKeyMode = "auto" | "manual";

/**
 * 充值表单:两种录入场景 + 到期日设置。充值成功后把所用 Kiro 用户在 kiro_users
 * 台账里同步为 in_use=true 并回写到期日(markFleetConsumed)。
 */
function AppendKeysForm({
  instanceId,
  defaultExpiresAt,
  onDone,
}: {
  instanceId: string;
  defaultExpiresAt: string;
  onDone: () => void;
}) {
  const [keyMode, setKeyMode] = useState<AppendKeyMode>("auto");
  const [autoEntries, setAutoEntries] = useState<AutoCreateEntry[]>([]);
  const [keyEntries, setKeyEntries] = useState<KeyEntryDraft[]>([emptyKeyEntry()]);
  const [expiresAt, setExpiresAt] = useState<string>(defaultExpiresAt);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const [tier, setTier] = useState("");
  const [candidates, setCandidates] = useState<RecommendUserCandidateDto[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateError, setCandidateError] = useState<string | null>(null);

  const handleTierChange = async (value: string) => {
    setTier(value);
    setCandidateError(null);
    setCandidates([]);
    if (!value) return;
    setLoadingCandidates(true);
    try {
      const resp = await recommendUsers(value);
      setCandidates(resp.candidates);
    } catch (err) {
      setCandidateError(err instanceof Error ? err.message : "获取推荐用户失败");
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleUseCandidate = (candidate: RecommendUserCandidateDto) => {
    const exists = autoEntries.some((e) => e.email === candidate.email);
    if (!exists) {
      setAutoEntries((prev) => [
        ...prev,
        { account_id: candidate.account_id, email: candidate.email, username: candidate.username },
      ]);
    }
  };

  const handleAddRow = () => setKeyEntries((prev) => [...prev, emptyKeyEntry()]);
  const handleRemoveRow = (idx: number) => setKeyEntries((prev) => prev.filter((_, i) => i !== idx));
  const handleUpdateRow = (idx: number, patch: Partial<KeyEntryDraft>) =>
    setKeyEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));

  /** 充值成功后同步 kiro_users(到期日 + in_use);失败只提示,不影响充值本身。 */
  const syncConsumed = async (emails: string[]) => {
    const uniqueEmails = Array.from(new Set(emails.filter((e) => e)));
    if (uniqueEmails.length === 0 || !expiresAt) return;
    try {
      await markFleetConsumed(uniqueEmails.map((email) => ({ email, expires_at: expiresAt })));
    } catch {
      /* 同步失败不阻断充值结果展示 */
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!expiresAt) {
      setError("请选择本次充值的到期日期");
      return;
    }
    if (new Date(`${expiresAt}T00:00:00Z`).getTime() <= Date.now()) {
      setError("到期时间必须晚于当前时间");
      return;
    }

    if (keyMode === "auto") {
      if (autoEntries.length === 0) {
        setError("请至少选择 1 个推荐 Kiro 用户");
        return;
      }
      if (autoEntries.length > MAX_APPEND_KEYS) {
        setError(`单次追加数量不能超过 ${MAX_APPEND_KEYS} 个`);
        return;
      }
      setError("正在自动创建 Kiro API Key，请稍候...");
      setSubmitting(true);
      try {
        const resolvedKeys: FleetApiKeyEntry[] = [];
        const autoErrors: string[] = [];
        for (const entry of autoEntries) {
          try {
            const resp = await createKiroApiKey(entry.email, entry.account_id);
            if (resp.status === "SUCCESS" && resp.ksk_key) {
              resolvedKeys.push({
                key: resp.ksk_key,
                kiro_user_email: entry.email,
                account_id: entry.account_id,
                key_name: resp.key_name,
              });
            } else {
              autoErrors.push(`${entry.email}：${resp.error_detail ?? "自动创建失败(NEEDS_MANUAL)"}`);
            }
          } catch (err) {
            autoErrors.push(`${entry.email}：${err instanceof Error ? err.message : "请求失败"}`);
          }
        }
        if (autoErrors.length > 0) {
          setError("部分用户 Key 创建失败：\n" + autoErrors.join("\n"));
          return;
        }
        setError(null);
        const resp = await appendKeys(instanceId, resolvedKeys, expiresAt);
        await syncConsumed(autoEntries.map((e) => e.email));
        setResult(`追加成功,本次新增 ${resp.added_count} 个,当前累计 ${resp.kiro_api_key_count} 个`);
        setAutoEntries([]);
        onDone();
      } catch (err) {
        setError(err instanceof Error ? err.message : "追加失败");
      } finally {
        setSubmitting(false);
      }
    } else {
      const nonEmptyEntries = keyEntries.filter((entry) => entry.key.trim().length > 0);
      if (nonEmptyEntries.length === 0) {
        setError("请至少录入 1 个 Kiro_API_Key");
        return;
      }
      if (nonEmptyEntries.length > MAX_APPEND_KEYS) {
        setError(`单次追加数量不能超过 ${MAX_APPEND_KEYS} 个`);
        return;
      }
      const badFormatIdx = nonEmptyEntries.findIndex((e) => !e.key.trim().startsWith("ksk_"));
      if (badFormatIdx >= 0) {
        setError(`第 ${badFormatIdx + 1} 个 Kiro_API_Key 格式不合法(必须以 ksk_ 为前缀)`);
        return;
      }
      const missingEmailIdx = nonEmptyEntries.findIndex((e) => !e.email.trim());
      if (missingEmailIdx >= 0) {
        setError(`第 ${missingEmailIdx + 1} 个 Kiro_API_Key 缺少关联的 Kiro 用户邮箱`);
        return;
      }
      const badEmailIdx = nonEmptyEntries.findIndex((e) => !isValidEmail(e.email.trim()));
      if (badEmailIdx >= 0) {
        setError(`第 ${badEmailIdx + 1} 个 Kiro_API_Key 关联的邮箱格式不合法`);
        return;
      }
      setSubmitting(true);
      try {
        const resp = await appendKeys(
          instanceId,
          nonEmptyEntries.map((e) => ({ key: e.key.trim(), kiro_user_email: e.email.trim() })),
          expiresAt
        );
        await syncConsumed(nonEmptyEntries.map((e) => e.email.trim()));
        setResult(`追加成功,本次新增 ${resp.added_count} 个,当前累计 ${resp.kiro_api_key_count} 个`);
        setKeyEntries([emptyKeyEntry()]);
        onDone();
      } catch (err) {
        setError(err instanceof Error ? err.message : "追加失败");
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="error-banner" style={{ whiteSpace: "pre-line" }}>
          {error}
        </div>
      )}
      {result && (
        <div className="card" style={{ background: "#f0fdf4", color: "#166534" }}>
          {result}
        </div>
      )}

      <div className="form-row">
        <label>本次充值到期日期</label>
        <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        <span style={{ color: "#64748b", fontSize: 11 }}>充值成功后将同步该到期日到关联 Kiro 用户</span>
      </div>

      <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input type="radio" checked={keyMode === "auto"} onChange={() => setKeyMode("auto")} />
          <span>推荐 Kiro 用户<span style={{ fontSize: 11, color: "#64748b", marginLeft: 4 }}>(自动生成 ksk_)</span></span>
        </label>
        <label style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer" }}>
          <input type="radio" checked={keyMode === "manual"} onChange={() => setKeyMode("manual")} />
          <span>手动录入 Kiro_API_Key</span>
        </label>
      </div>

      {keyMode === "auto" && (
        <div>
          {autoEntries.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>已选用户（创建时自动生成 ksk_）：</div>
              {autoEntries.map((entry) => (
                <div key={entry.email} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontFamily: "monospace" }}>{entry.email}</span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>({entry.username})</span>
                  <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                    onClick={() => setAutoEntries((prev) => prev.filter((e) => e.email !== entry.email))}>
                    移除
                  </button>
                </div>
              ))}
            </div>
          )}
          {autoEntries.length === 0 && (
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 8px" }}>
              请在下方推荐列表中选择用户，点击"使用"添加
            </p>
          )}
          <div className="form-row" style={{ marginTop: 4 }}>
            <label>按层级筛选推荐用户</label>
            <select value={tier} onChange={(e) => handleTierChange(e.target.value)}>
              <option value="">全部层级</option>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {loadingCandidates && <p style={{ fontSize: 12, color: "#64748b" }}>加载中...</p>}
          {candidateError && <span style={{ color: "#dc2626", fontSize: 12 }}>{candidateError}</span>}
          {!loadingCandidates && tier && !candidateError && (
            <ul style={{ fontSize: 12, paddingLeft: 16, marginTop: 4 }}>
              {candidates.length === 0 && <li style={{ listStyle: "none", marginLeft: -16 }}>暂无候选用户</li>}
              {candidates.map((c) => {
                const alreadySelected = autoEntries.some((e) => e.email === c.email);
                return (
                  <li key={c.account_id}>
                    {c.email}({c.username}){" "}
                    <button type="button" style={{ fontSize: 11, padding: "1px 6px" }}
                      onClick={() => handleUseCandidate(c)} disabled={alreadySelected}>
                      {alreadySelected ? "已选" : "使用"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {keyMode === "manual" && (
        <div>
          {keyEntries.map((entry, idx) => (
            <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 4, alignItems: "center" }}>
              <input
                style={{ width: 200, fontFamily: "monospace" }}
                value={entry.key}
                onChange={(e) => handleUpdateRow(idx, { key: e.target.value })}
                placeholder="ksk_xxx"
              />
              <input
                style={{ width: 200 }}
                value={entry.email}
                onChange={(e) => handleUpdateRow(idx, { email: e.target.value })}
                placeholder="关联的 Kiro 用户邮箱"
              />
              <button type="button" onClick={() => handleRemoveRow(idx)} disabled={keyEntries.length <= 1}>
                移除
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddRow}>添加 Key</button>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? "提交中..." : "追加"}
        </button>
      </div>
    </form>
  );
}

const RESEND_SECTIONS: { key: string; label: string }[] = [
  { key: "admin_panel", label: "管理面板地址" },
  { key: "api_endpoint", label: "API 端点" },
  { key: "expires_at", label: "到期日" },
  { key: "admin_password", label: "管理面板密码" },
  { key: "topup_info", label: "充值信息(账号池 Key 总数)" },
  { key: "client_api_key", label: "Client API Key 手动设置提示" },
];
const DEFAULT_RESEND_SECTIONS = ["admin_panel", "api_endpoint", "expires_at", "admin_password", "client_api_key"];

function ResendNotificationButton({ instanceId }: { instanceId: string }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set(DEFAULT_RESEND_SECTIONS));

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleResend = async () => {
    if (selected.size === 0) {
      setError("请至少勾选一项要发送的内容");
      return;
    }
    setSending(true);
    setError(null);
    setSent(null);
    try {
      const resp = await resendNotification(instanceId, Array.from(selected));
      setSent(resp.sent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "发送失败");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: "#475569" }}>向该实例的使用人邮箱重新发送通知,勾选要包含的内容:</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
        {RESEND_SECTIONS.map((s) => (
          <label key={s.key} style={{ width: "auto", display: "flex", gap: 4, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={selected.has(s.key)} onChange={() => toggle(s.key)} />
            {s.label}
          </label>
        ))}
      </div>
      {error && <div className="error-banner">{error}</div>}
      {sent === true && <p style={{ color: "#16a34a", fontSize: 13 }}>✓ 邮件已发送</p>}
      {sent === false && <p style={{ color: "#dc2626", fontSize: 13 }}>发送失败(请检查 SES 沙盒设置)</p>}
      <button className="primary" onClick={handleResend} disabled={sending}>
        {sending ? "发送中..." : "重新发送通知邮件"}
      </button>
    </div>
  );
}

function renderRecoveryGuide(publicIp: string | undefined): { available: boolean; text?: string; reason?: string } {
  if (!publicIp || publicIp === "-") {
    return { available: false, reason: "台账中未记录该实例的公网 IP 地址,无法生成人工恢复操作指引文本" };
  }
  const text =
    `通过 SSH 登录该 Kiro-Go 实例(公网 IP: ${publicIp}):\n` +
    `  ssh root@${publicIp}\n` +
    `登录后重写管理密码配置并重启容器:\n` +
    `  docker exec kiro-go sh -c "sed -i 's/^ADMIN_PASSWORD=.*/ADMIN_PASSWORD=<在此填写新密码>/' /app/data/config.json"\n` +
    `  docker restart kiro-go\n` +
    `完成后,请在 Fleet Console 详情页使用「标记已手动恢复」提交新密码。`;
  return { available: true, text };
}

function MarkRecoveredForm({ instanceId, onDone }: { instanceId: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !password.trim()) {
      setError("需要录入新密码后才能完成标记");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await markRecovered(instanceId, password);
      setPassword("");
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "标记失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="error-banner">{error}</div>}
      <div className="form-row">
        <label>新 Kiro_Go_Admin_Password</label>
        <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: 260 }} />
      </div>
      <button className="primary" type="submit" disabled={submitting}>
        {submitting ? "提交中..." : "标记已手动恢复"}
      </button>
    </form>
  );
}

/**
 * Kiro-Go 实例详情页。Admin 用户加载时额外拉取 getCredentials(ADMIN_ONLY)拿到
 * 真实的接入信息(API 端点/管理面板地址/Kiro_API_Key 明细)——get_instance 出于
 * 安全对所有人按非 Admin 视角投影,这些字段是占位符,故 Admin 视角需单独取凭证。
 */
export function FleetDetailPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const { isAdmin } = useAuth();
  const detail = useAsync<FleetInstanceViewDto>(() => getInstance(instanceId ?? ""), [instanceId]);

  const [creds, setCreds] = useState<FleetInstanceViewDto | null>(null);
  const loadCreds = () => {
    if (isAdmin && instanceId) {
      getCredentials(instanceId)
        .then(setCreds)
        .catch(() => setCreds(null));
    }
  };
  useEffect(() => {
    loadCreds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, isAdmin, detail.data?.status]);

  if (!instanceId) return <p>缺少实例标识</p>;
  if (detail.loading) return <p>加载中...</p>;
  if (detail.error) return <div className="error-banner">{detail.error}</div>;
  if (!detail.data) return null;

  const inst = detail.data;
  // 敏感字段(接入信息 + Key 明细)用 Admin 凭证视角的数据(若已加载),否则用占位符视角。
  const view = creds ?? inst;
  const isReady = inst.status === "READY";
  const expiresAtDate = (inst.expires_at || "").slice(0, 10);
  const guide = inst.credential_drift ? renderRecoveryGuide(inst.public_ip) : null;

  const reloadAll = () => {
    detail.reload();
    loadCreds();
  };

  return (
    <div>
      <h2 className="section-title">实例详情:{inst.instance_id}</h2>

      <div className="card">
        <div className="form-row">
          <label>状态</label>
          <span className="badge badge-pending">{inst.status}</span>
        </div>
        <div className="form-row">
          <label>凭证状态</label>
          <span>
            {!isReady ? (
              <span style={{ color: "#94a3b8" }}>-(实例未就绪)</span>
            ) : inst.credential_drift ? (
              <span className="badge badge-warn">凭证可能已失效</span>
            ) : (
              <span className="badge badge-active">正常</span>
            )}
          </span>
        </div>
        <div className="form-row">
          <label>使用人邮箱</label>
          <span>{inst.assigned_user_email}</span>
        </div>
        <div className="form-row">
          <label>名称/备注</label>
          <span>{inst.name_note ?? "-"}</span>
        </div>
        <div className="form-row">
          <label>部署区域</label>
          <span>{inst.region}</span>
        </div>
        <div className="form-row">
          <label>公网 IP</label>
          <span>{inst.public_ip}</span>
        </div>
        <div className="form-row">
          <label>启动完成时间</label>
          <span>{formatBeijingTime(inst.ready_at)}</span>
        </div>
        <div className="form-row">
          <label>到期时间</label>
          <span>{formatBeijingDate(inst.expires_at)}</span>
        </div>
        <div className="form-row">
          <label>创建时间</label>
          <span>{formatBeijingTime(inst.created_at)}</span>
        </div>
      </div>

      {inst.credential_drift && (
        <div className="card" style={{ background: "#fee2e2", border: "1px solid #dc2626" }} data-testid="drift-banner">
          <strong>凭证可能已失效</strong>:该实例的管理凭证连续多次鉴权失败,可能是使用人自行更改了
          管理面板密码。
          {guide && guide.available ? (
            <pre style={{ whiteSpace: "pre-wrap", background: "#fff", padding: 8, marginTop: 8 }}>{guide.text}</pre>
          ) : (
            <p style={{ marginTop: 8 }}>{guide?.reason}</p>
          )}
        </div>
      )}

      <div className="card">
        <div className="section-title">接入信息</div>
        {!isReady && <p style={{ color: "#64748b", fontSize: 13 }}>实例尚未部署完成(READY),以下字段暂不可用。</p>}
        {isReady && !isAdmin && (
          <p style={{ color: "#64748b", fontSize: 13 }}>接入信息(含端点/密码)仅 Admin 可见。</p>
        )}
        {isReady && isAdmin && !creds && <p style={{ color: "#64748b", fontSize: 13 }}>接入信息加载中...</p>}
        <CopyableRow label="API 端点地址(基础)" field={view.api_endpoint_url} />
        <EndpointRow label="Claude API" base={view.api_endpoint_url.value} suffix="/v1/messages" hint="Anthropic /v1/messages 兼容" />
        <EndpointRow label="OpenAI API" base={view.api_endpoint_url.value} suffix="/v1/chat/completions" hint="OpenAI /v1/chat/completions 兼容" />
        <EndpointRow label="OpenAI Responses API" base={view.api_endpoint_url.value} suffix="/v1/responses" hint="OpenAI Responses /v1/responses 兼容" />
        <CopyableRow label="管理面板地址" field={view.admin_panel_url} />
        <AdminPasswordCell instanceId={inst.instance_id} field={view.kiro_go_admin_password} />
        {/* Client API Key 是人工设置项,无真实值,不提供复制按钮 */}
        <div className="form-row" style={{ alignItems: "flex-start" }}>
          <label>Kiro_Go_Client_API_Key</label>
          <span style={{ color: "#dc2626", fontSize: 12, flex: "1 1 auto" }}>
            {view.kiro_go_client_api_key.caption ?? "需登录管理面板手动设置"}
          </span>
        </div>
      </div>

      <div className="card">
        <div className="section-title">Kiro_API_Key</div>
        {isReady && !isAdmin && <p style={{ color: "#64748b", fontSize: 13 }}>Kiro_API_Key 明细仅 Admin 可见。</p>}
        <KiroApiKeySection entries={view.kiro_api_key_entries} />
      </div>

      {isReady && (
        <div className="card">
          <div className="section-title">追加 Kiro_API_Key(充值)</div>
          <AppendKeysForm instanceId={inst.instance_id} defaultExpiresAt={expiresAtDate} onDone={reloadAll} />
        </div>
      )}

      {isReady && (
        <div className="card">
          <div className="section-title">重新发送通知邮件</div>
          <ResendNotificationButton instanceId={inst.instance_id} />
        </div>
      )}

      {inst.credential_drift && (
        <div className="card">
          <div className="section-title">标记已手动恢复</div>
          <MarkRecoveredForm instanceId={inst.instance_id} onDone={reloadAll} />
        </div>
      )}
    </div>
  );
}
